import type { RefObject } from 'react'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'

import type {
  AccommodationDayPickerSize,
  AccommodationMobileDayCellSelectHandler,
} from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendar.types'
import AccommodationCalendarDayPicker from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarDayPicker'
import type { AccommodationDayCellVariant } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarLargeDayCell'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import type { FeaturedDateRange } from 'lib/datetime/calendarDayViews'
import { generateStaticDayViews } from 'lib/datetime/calendarDayViews'
import { generateStaticMonthCaptions } from 'lib/datetime/calendarMonthCaptions'
import noop from 'lib/function/noop'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'
import { getCalendarDayPrices } from 'selectors/offerPage/propertyOfferPageSelectors'

interface Props {
  /** Destination time zone. Determines earliest permissible check-in date. */
  propertyTimezone?: string
  featuredDateRanges?: Utils.NonEmptyArray<FeaturedDateRange> | undefined
  dayPrices?: { [isoDate: string]: App.Property.CalendarDay } | undefined
  displayLuxPlusPrice: boolean
  checkInDate?: string
  checkOutDate?: string
  /**
   * Reference to the scroll container element this picker sits in
   * This is necessary to provide so it can manage showing the correct month
   */
  scrollerRef: RefObject<HTMLElement | null>
  size: AccommodationDayPickerSize
  /**
   * The month we're picking the date for inso ISO date format
   */
  selectedMonth?: string
  calendarStartDate?: string

  /**
   * Event for when the selected dates change
   */
  onDatesChange?: AccommodationMobileDayCellSelectHandler

  availabilityVariantMap?: Map<string, AccommodationDayCellVariant>
  renderFromStart?: boolean
  /** Prices are approximate 1 night prices: each month gets a caption saying so and days drop the "1 night from" label */
  isIndicativePriceCalendar?: boolean
}

/**
 * Targets the promotion, but keeps customers on their own check-in month once it already
 * falls on or after the target, so they are never sent back to an earlier month.
 */
function resolvePromotionIsoDate(checkInIsoDate: string | undefined, targetIsoDate: string | undefined) {
  if (!targetIsoDate) return undefined
  return (checkInIsoDate && checkInIsoDate >= targetIsoDate) ? checkInIsoDate : targetIsoDate
}

export function OfferDatePicker({
  propertyTimezone,
  featuredDateRanges,
  dayPrices,
  displayLuxPlusPrice,
  checkInDate,
  scrollerRef,
  checkOutDate,
  size,
  selectedMonth,
  calendarStartDate,
  onDatesChange = noop,
  availabilityVariantMap,
  renderFromStart,
  isIndicativePriceCalendar,
}: Props) {
  const intl = useIntl()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const onDatesSelect = useCallback<AccommodationMobileDayCellSelectHandler>(day => {
    onDatesChange(day)
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.DateChanged)
  }, [onDatesChange, onUserAnalyticsAction])

  const staticDayViews = useMemo(() => generateStaticDayViews(propertyTimezone, featuredDateRanges, dayPrices, displayLuxPlusPrice), [propertyTimezone, featuredDateRanges, dayPrices, displayLuxPlusPrice])
  // A day nobody can check in on can still end a stay, so once only a check-in is picked, later sold out days are pickable.
  const dayViews = useMemo(() => {
    if (!checkInDate || checkOutDate) return staticDayViews
    return staticDayViews.map(day => day.soldOut && day.date > checkInDate ? { ...day, soldOut: false } : day)
  }, [staticDayViews, checkInDate, checkOutDate])
  const staticMonthCaptions = useMemo(() => isIndicativePriceCalendar ? generateStaticMonthCaptions(intl, dayPrices) : undefined, [isIndicativePriceCalendar, intl, dayPrices])
  const firstFeaturedIsoDate = staticDayViews.find(day => day.featured)?.date
  const initialDate = selectedMonth ?? resolvePromotionIsoDate(checkInDate, calendarStartDate ?? firstFeaturedIsoDate)
  const month = useMemo(() => {
    if (!initialDate) return undefined
    // Match a rendered day so past promotion starts resolve to the current month.
    const day = staticDayViews.find(view => view.date >= initialDate)
    return day ? new Date(day.year, day.month, 1) : undefined
  }, [initialDate, staticDayViews])

  useEffect(() => {
    if (!month) {
      scrollerRef.current?.scrollTo({ behavior: 'auto', top: 0 })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [month])

  return <AccommodationCalendarDayPicker
    key={month ? `${month.getFullYear()}-${month.getMonth()}` : 'default'}
    startDate={checkInDate}
    endDate={checkOutDate}
    onDaySelect={onDatesSelect}
    contentRef={scrollerRef}
    views={dayViews}
    monthCaptions={staticMonthCaptions}
    initialMonth={month?.getMonth()}
    initialYear={month?.getFullYear()}
    size={size}
    availabilityVariantMap={availabilityVariantMap}
    renderFromStart={renderFromStart}
    hideDurationLabel={isIndicativePriceCalendar}
  />
}

type MappedProps = Pick<Props, 'dayPrices' | 'displayLuxPlusPrice' | 'calendarStartDate'> & { open: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  calendarStartDate: state.activeFilter === 'checkin' ? state.calendarStartDate : undefined,
  dayPrices: getCalendarDayPrices(state, appState),
  displayLuxPlusPrice: !!state.displayMemberPrice,
  open: !!state.activeFilter || state.activeHeaderFilter === 'checkin',
}), OfferDatePicker, preventRenderWithClosedFilter)
